import { z } from 'zod';
import { moneySchema } from './orders.ts';

/**
 * Monthly commission statements, as the portal sees them.
 *
 * A statement is a figure a partner has been SHOWN, which is what makes it immutable:
 * the monthly run freezes it, and anything arriving afterwards — a refund on an order
 * already settled — lands on the next one as a `RECOVERY` rather than editing this one.
 * The schema reflects that: every amount is a fact about a closed month, and the only
 * field that ever changes after generation is the payment.
 */

/** `2026-10` — the month whose commission matured, not the month the run happened. */
export const periodSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'expected a YYYY-MM period');

export const statementStatusSchema = z.enum(['generated', 'paid']);
export type StatementStatusValue = z.infer<typeof statementStatusSchema>;

export const statementSchema = z.object({
  id: z.string(),
  partnerId: z.string(),
  /** Joined for the staff list, where rows from several partners sit together. */
  partnerName: z.string(),
  period: periodSchema,

  /**
   * Brought forward from the partner's last statement. Zero or NEGATIVE, never
   * positive: a positive balance would mean money owed that was not paid out, which
   * cannot happen — the run pays what has matured.
   */
  openingBalance: moneySchema,
  /** Accruals that matured in this period. */
  earned: moneySchema,
  /** Claw-backs on orders refunded before they were settled. Zero or negative. */
  adjusted: moneySchema,
  /** Claw-backs on orders already PAID OUT in an earlier period. Zero or negative. */
  recovered: moneySchema,
  /** `earned + adjusted + recovered`. */
  net: moneySchema,
  /** What Sparkery owes for this month: `max(0, openingBalance + net)`. */
  payable: moneySchema,
  /**
   * What carries into next month: `min(0, openingBalance + net)`.
   *
   * Sparkery never invoices a partner, so a month that nets negative is not a bill —
   * the debit follows them until their earnings clear it.
   */
  carriedForward: moneySchema,

  status: statementStatusSchema,
  generatedAt: z.string(),
  paidAt: z.string().nullable(),
  /** The bank reference an admin recorded against the payment. */
  paidReference: z.string().nullable(),
  paidBy: z.string().nullable(),
  /** Ledger movements this statement settled, for the "N movements" caption. */
  entryCount: z.number().int(),
});
export type Statement = z.infer<typeof statementSchema>;

/**
 * What a movement was: the ledger's `kind`, said in words.
 *
 * The four kinds mean four different things to a partner reading their statement, and
 * "ADJUSTMENT" is not one of them. `recovery` in particular has to be legible on its
 * own — it is the only line that refers to a month the partner has already been paid
 * for, which is exactly the line they will query.
 */
export const movementKindSchema = z.enum(['commission', 'refund', 'cancelled', 'recovery']);
export type MovementKind = z.infer<typeof movementKindSchema>;

export const statementMovementSchema = z.object({
  id: z.string(),
  kind: movementKindSchema,
  /** Signed: negative for a refund, a cancellation or a recovery. */
  amount: moneySchema,

  /** The order this movement is about. A movement without it explains nothing. */
  orderId: z.string(),
  orderName: z.string(),
  customerLabel: z.string().nullable(),
  /**
   * The order value the commission was computed on, frozen at accrual.
   *
   * NOT the order's current total: an order refunded down to zero still earned what it
   * earned, and a statement that showed today's total beside a historic commission
   * figure would look like an arithmetic error.
   */
  orderValue: moneySchema,
  /** The rate applied, also frozen — so a rate change cannot make history look wrong. */
  rate: z.string(),

  /** When the underlying event happened: the sale, or the refund. */
  occurredAt: z.string(),
  /** When it became payable, which is why it landed in THIS statement. */
  maturesAt: z.string(),
  /** The order's state now. A refunded order whose commission stood is worth seeing. */
  orderState: z.enum(['confirmed', 'pending', 'refunded', 'cancelled']),
});
export type StatementMovement = z.infer<typeof statementMovementSchema>;

export const statementDetailSchema = z.object({
  statement: statementSchema,
  /**
   * Every movement this statement settled, oldest maturity first.
   *
   * These SUM to the statement's `net` — the assertion the drill-down exists to make
   * checkable. What they do not include is the opening balance, which came from an
   * earlier statement rather than from an order.
   */
  movements: z.array(statementMovementSchema),
});
export type StatementDetail = z.infer<typeof statementDetailSchema>;

export const statementsResponseSchema = z.object({
  /** Newest period first. Scoped server-side: a partner only ever sees their own. */
  statements: z.array(statementSchema),
  /** Null when the viewer sees every partner at once. */
  partnerId: z.string().nullable(),
});
export type StatementsResponse = z.infer<typeof statementsResponseSchema>;

export const markPaidInputSchema = z.object({
  /**
   * The payment reference — a BACS reference, a transfer id, whatever the finance side
   * used. Required, because a statement marked paid with nothing to trace it to is a
   * statement nobody can reconcile.
   */
  reference: z.string().trim().min(1, 'a payment reference is required').max(64),
});
export type MarkPaidInput = z.infer<typeof markPaidInputSchema>;

export const markPaidResultSchema = z.object({
  /**
   * `already-paid` is reported rather than treated as success, so a second click cannot
   * quietly replace the reference of a payment that has already gone out.
   */
  outcome: z.enum(['paid', 'already-paid']),
  statement: statementSchema,
});
export type MarkPaidResult = z.infer<typeof markPaidResultSchema>;
